'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import styles from './styles.module.css'

interface ServicesMenuProps {
  isOpen: boolean
  onClose: () => void
  onConsultationClick: () => void
  onProjectClick: () => void
}

const ServicesMenu: React.FC<ServicesMenuProps> = ({ isOpen, onClose, onConsultationClick, onProjectClick }) => {
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Управляем анимацией и прокруткой
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Небольшая задержка для плавного появления
      setTimeout(() => {
        setIsAnimating(true)
      }, 10)
      document.body.style.overflow = 'hidden'
    } else {
      setIsAnimating(false)
      // Задержка перед скрытием для завершения анимации
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 300)
      document.body.style.overflow = ''
      return () => clearTimeout(timer)
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Обработчик ESC клавиши
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const handleComplexSolutionsClick = () => {
    router.push('/services/complex-solutions')
    onClose()
  }

  const handleWebAppsClick = () => {
    router.push('/services/web-apps')
    onClose()
  }
  const handleIntegrationsClick = () => {
    router.push('/services/integrations')
    onClose()
  }

  const handleSupportClick = () => {
    router.push('/services/support')
    onClose()
  }

  const handleOutstaffingClick = () => {
    router.push('/services/outstaffing')
    onClose()
  }

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop with blur - только под хедером */}
      <div 
        className={`${styles.backdrop} ${isAnimating ? styles.show : ''}`}
        onClick={onClose}
      />
      
      {/* Menu overlay */}
      <div 
        className={`${styles.menuOverlay} ${isAnimating ? styles.show : ''}`}
      >
        <div className={styles.menuContent}>
          {/* Block 1 - Заказная разработка */}
          <div className={styles.block}>
            <h3 className={styles.blockTitle} style={{ marginBottom: '20px' }}>
              Заказная разработка
            </h3>
            <div className={styles.blockItems}>
              <div className={styles.blockItem} onClick={handleWebAppsClick}>
                <span className={styles.blockItemText}>Сайты и приложения</span>
              </div>
              <div className={styles.blockItem} onClick={handleIntegrationsClick}>
                <span className={styles.blockItemText}>Интеграции</span>
              </div>
              <div className={styles.blockItem} onClick={handleComplexSolutionsClick}>
                <span className={styles.blockItemText}>Комплексные решения</span>
              </div>
            </div>
          </div>

          {/* Block 2 - Техническое сопровождение */}
          <div className={styles.block}>
            <div className={styles.blockItemsLarge}>
              <div className={styles.blockItemLarge} onClick={handleSupportClick}>
                <span className={styles.blockItemLargeText}>Техническое сопровождение</span>
              </div>
              <div className={styles.blockItemLarge} onClick={onConsultationClick}>
                <span className={styles.blockItemLargeText}>Технологическая экспертиза</span>
              </div>
              <div className={styles.blockItemLarge} onClick={handleOutstaffingClick}>
                <span className={styles.blockItemLargeText}>Аутстаффинг</span>
              </div>
            </div>
          </div>

          {/* Block 3 - Смотреть все услуги */}
          <div className={styles.blockRight}>
            <Link href="/services" className={styles.allServicesLink}>
              <span className={styles.allServicesLinkText}>Смотреть все услуги</span>
              <svg className={styles.arrowIcon} viewBox="0 0 16 16" fill="none">
                <path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="currentColor" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

ServicesMenu.displayName = 'ServicesMenu'

export default ServicesMenu